//
// Copyright (c) 2025 Comcast
//

import { useTranslation } from 'react-i18next'
import { useParams, Link } from 'react-router'
import { useSelector } from 'react-redux'
import { useState } from 'react'
import styled from 'styled-components'
import startCase from 'lodash/startCase'
import isEmpty from 'lodash/isEmpty'

import {
  useFetchTicketQuery,
  useCreateTicketCommentMutation,
} from 'api/tickets'

import { RouteTicketingSystems, RouteTicketingSystemsByPartner, RouteTicketingSystemsTickets } from 'routes'
import { getFieldValue } from 'utils/ticketFields'
import * as sessionUserSelector from 'selectors/sessionUser'
import * as ticketSelector from 'selectors/ticket'

import colors from 'components/utils/colors'
import { capitalizeFirstLetter, isUrlValid } from 'utils/strings'

import ErrorPage404 from 'components/App/ErrorPage/ErrorPage404'
import MainWrap from 'components/Shared/Skeleton/MainWrap/Index'
import Content from 'components/Shared/Skeleton/Content/Index'
import ScrollableTabsPanel from 'components/App/ScrollableTabsPanel/Index'
import Header from 'components/Shared/Header/Index'
import HtmlContent from 'components/Shared/HtmlContent/Index'
import Table from 'components/Shared/TableNew/Index'
import TableBody from 'components/Shared/TableNew/TableBody'
import TableFormRow from 'components/Shared/TableForm/TableFormRow'
import TableFormTableField from 'components/Shared/TableForm/TableFormTableField'
import TableFormLabelCell from 'components/Shared/TableForm/TableFormLabelCell'
import TableFormValueCell from 'components/Shared/TableForm/TableFormValueCell'
import TableCellWithActions from 'components/Shared/TableNew/TableCellWithActions'
import ShowOnRowHover from 'components/Shared/TableNew/ShowOnRowHover'
import TicketPageSidebar from 'components/App/TicketPage/TicketPageSidebar/Index'
import ButtonsToolbar from './ButtonsToolbar/Index'
import TicketChildren from './TicketChildren/Index'
import TicketAttachments from './TicketAttachments/Index'
import TicketComments from './TicketComments/Index'
import CopyIcon from 'components/Shared/CopyIcon/Index'
import Tooltip from 'components/Shared/Tooltip/Index'
import TableCellAction from 'components/Shared/TableNew/TableCellAction'
import TicketTypeBadge from 'components/App/TicketsPage/shared/TicketTypeBadge'
import Badge from 'components/Shared/Badge/Index'
import RichTextEditor from 'components/Shared/RichTextEditor/Index'

const StyledFieldText = styled.div`
  font-size: 14px;
  line-height: 24px;
  color: ${colors.secondary20};
  white-space: pre-line;
  word-break: break-word;
`

const StyledNoDataText = styled.section`
  font-size: 14px;
  line-height: 16px;
  font-weight: 400;
  color: ${colors.secondary70};
  line-height: 16px;
`

const Spacer = styled.div`
  height: 22px;
`

const ActionsCellContent = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
`

const TypeFieldValue = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  color: ${colors.secondary20};
`

const ArrayValuesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`

const TicketPage = () => {
  const { t } = useTranslation()
  const params = useParams()
  const [secureFieldsVisibility, setSecureFieldsVisibility] = useState({})

  const ticketingSystemDisplayId = params.ticketingSystemDisplayId || null
  const ticketId = params.ticketId || null

  const userTimeZone = useSelector(sessionUserSelector.timezone)
  const isUserPrivileged = useSelector(sessionUserSelector.isUserPrivileged)

  const isUpdating = useSelector(ticketSelector.isUpdating)

  const { data, isFetching } = useFetchTicketQuery({
    ticketingSystemDisplayId: ticketingSystemDisplayId,
    ticketId: ticketId,
  })
  const [createComment, { isLoading: isCreatingComment }] = useCreateTicketCommentMutation()

  const ticket = data?.ticket || {}
  const editableFields = ticket?.bodyFields || []
  const relatedSystems = data?.metaData?.relatedSystems || null
  const partnerId = ticket?.partnerId || null
  const partnerName = capitalizeFirstLetter(ticket?.partnerName || '')

  const triggreSecureFieldVisibility = (fieldName) => {
    setSecureFieldsVisibility(prevState => ({
      ...prevState,
      [fieldName]: !prevState[fieldName],
    }))
  }

  const getFieldText = (fieldName, fieldText, isSecureField) => {
    if (!isSecureField) {
      return fieldText
    }

    return secureFieldsVisibility[fieldName] ? fieldText : fieldText.replace(/./g, '•')
  }

  const renderTicketField = (field, fieldText, isSecureField) => {
    if (fieldText === '') {
      return <StyledNoDataText>{t('ticketPage.tableNoData')}</StyledNoDataText>
    } else if (field.fieldType === 'html') {
      return <HtmlContent html={fieldText} />
    } else if (field.fieldType === 'array') {
      return (
        <ArrayValuesList>
          {field.value?.map(v => <Badge margin='0' key={v}>{v}</Badge>)}
        </ArrayValuesList>
      )
    } else if (field.fieldType === 'url' && isUrlValid(fieldText)) {
      return <Link to={fieldText} target='_blank'>{fieldText}</Link>
    } else if (ticket.hierarchyEnabled && field.name === 'type') {
      return (
        <TypeFieldValue>
          <TicketTypeBadge ticketType={ticket.type} noMargin />
          {getFieldText(field.name, fieldText, isSecureField)}
        </TypeFieldValue>
      )
    } else if (field.fieldType === 'text_jira') {
      return <RichTextEditor isReadOnly value={field.value} />
    } else {
      return (
        <StyledFieldText>
          {getFieldText(field.name, fieldText, isSecureField)}
        </StyledFieldText>
      )
    }
  }

  const renderFieldsTable = () => {
    const renderValueCell = (field, fieldText) => {
      const isSecureField = field.fieldType === 'encrypted_text'
      const showSecureIcon = isSecureField && fieldText
      return (
        <>
          <TableFormValueCell>
            {renderTicketField(field, fieldText, isSecureField)}
          </TableFormValueCell>
          <TableCellWithActions>
            <ActionsCellContent>
              <ShowOnRowHover>
                <CopyIcon copyText={fieldText} tooltipPlace='top' />
                {showSecureIcon && (
                  <>
                    <TableCellAction
                      onClick={() => triggreSecureFieldVisibility(field.name)}
                      iconName={secureFieldsVisibility[field.name] ? 'icon-hidden' : 'icon-visible'}
                      tooltip={secureFieldsVisibility[field.name] ? t('common.hideSecureInfo') : t('common.viewSecureInfo')}
                      tooltipId='trigger_secure_column'
                    />
                    <Tooltip id='trigger_secure_column' />
                  </>
                )}
              </ShowOnRowHover>
            </ActionsCellContent>
          </TableCellWithActions>
        </>
      )
    }

    return (
      <Table width='100%' maxWidth='1112px'>
        <TableBody hoverable>
          {editableFields.map(field => {
            const fieldText = getFieldValue(field, userTimeZone)
            return (
              <TableFormRow key={field.id}>
                {field.fieldType === 'table_field' && !isEmpty(field.value)
                  ? <TableFormTableField field={field} />
                  : (
                    <>
                      <TableFormLabelCell tooltip={field.tooltiptext}>
                        {startCase(field.name)}
                      </TableFormLabelCell>
                      {renderValueCell(field, fieldText)}
                    </>
                    )
                }
              </TableFormRow>
            )
          })}
        </TableBody>
      </Table>
    )
  }

  const getBreadcrumbs = () => {
    let ticketingSystemsUrl = RouteTicketingSystems()
    const ticketingSystemUrl = RouteTicketingSystemsTickets({ ticketingSystemDisplayId })

    let ticketingSystems = t('common.breadcrumbTicketingSystems')
    if (isUserPrivileged) {
      ticketingSystems = t('common.breadcrumbTicketingSystemsByPartner', { partnerName: partnerName })
      ticketingSystemsUrl = RouteTicketingSystemsByPartner({ partnerId: partnerId })
    }

    const breadcrumbsList = [{
      text: ticketingSystems,
      url: ticketingSystemsUrl,
    }, {
      text: ticket.ticketingSystemName,
      url: ticketingSystemUrl,
    }, {
      text: ticket.externalId,
    }]
    return breadcrumbsList
  }

  const isLoading = isFetching || isCreatingComment || isUpdating
  const title = ticket.title
  const ticketNotFound = data?.message?.type === 'error' && data?.message?.text === 'Ticket not found'
  let pageTitle
  if (!isEmpty(ticket)) {
    pageTitle = `${ticket.externalId} - ${title}`
  }

  if (ticketNotFound) {
    return <ErrorPage404 />
  }

  const fixedHeader = isUserPrivileged && (
    <ScrollableTabsPanel
      relatedSystems={relatedSystems}
      ticketingSystemDisplayId={ticketingSystemDisplayId}
    />
  )

  return (
    <MainWrap
      pageTitle={pageTitle}
      fixedHeader={fixedHeader}
    >
      <TicketPageSidebar ticket={ticket} />
      <Content loading={isLoading}>
        <Header
          title={title}
          breadcrumbsList={getBreadcrumbs()}
        />
        <ButtonsToolbar ticket={ticket} />

        <Spacer />

        {renderFieldsTable()}

        <Spacer />
        <TicketChildren ticket={ticket} />
        <TicketAttachments ticket={ticket} />
        <TicketComments
          ticket={ticket}
          createComment={createComment}
        />
      </Content>
    </MainWrap>
  )
}

export default TicketPage
